import * as THREE from 'three/webgpu';
import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchLineObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../../mesh/guards';
import {isSketchContour} from '../../../mesh/meshProxy/SketchContour';
import type {SketchContour} from '../../../mesh/meshProxy/SketchContour';
import type {SketchPointObject} from '../../../mesh/sketch/SketchPointObject';
import type {ReferencePlane} from '../../../reference/ReferencePlane';
import {SketchDimensionApplicationService} from '../application/SketchDimensionApplicationService';
import type {
    SketchDimensionHistoryTransactionPort,
    SketchDimensionSolverSession,
} from '../application/SketchDimensionApplicationService';
import {SketchDimensionValidationService} from '../application/SketchDimensionValidationService';
import {SketchDimensionIdentityService} from '../repository/SketchDimensionIdentityService';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';
import {resolveSketchDimensionQuantityKind} from '../model/SketchDimension';
import type {
    CreateSketchDimensionInput,
    SketchDimensionSnapshot,
} from '../SketchDimensionTypes';
import {rehearseSketchDimensionDrivingSolve} from './rehearseSketchDimensionDrivingSolve';
import {SketchDimensionInferenceRegistry} from './SketchDimensionInferenceRegistry';
import {registerBuiltinSmartDimensionInferenceStrategies} from './sketchDimensionInferenceStrategies';
import type {
    SmartDimensionCandidate,
    SmartDimensionCommandSnapshot,
    SmartDimensionCommandState,
    SmartDimensionDrivenDecisionContext,
    SmartDimensionPickItem,
    SmartDimensionPickModifiers,
    SmartDimensionSnapshotListener,
    SmartDimensionStartOptions,
    SmartDimensionVec2,
} from './SketchSmartDimensionTypes';

/** 活动草图命令上下文（宿主按需解析，控制器不缓存运行时对象）。 */
export interface SmartDimensionSketchContext {
    /** 草图持久 ID。 */
    readonly sketchId: string;
    /** 草图参考平面。 */
    readonly referencePlane: ReferencePlane;
    /** 尺寸仓库。 */
    readonly repository: SketchDimensionRepository;
    /** 收集草图内全部运行时点对象（参数解写回目标）。 */
    readonly collectRuntimePoints: () => readonly SketchPointObject[];
    /** 收集 solver island 采集种子对象（草图内全部实体/点）。 */
    readonly collectSeedObjects: () => readonly unknown[];
    /**
     * 尺寸驱动几何写回**终局提交**后的定义变更通知（触发拉伸等特征实时重建）。
     *
     * 只在正式事务提交后调用一次；Instant2D 逐帧预览的 commit/rollback 不通知，
     * 否则特征重建会被拖成逐帧全量重算。缺省不通知（测试假宿主）。
     */
    readonly notifyGeometryCommitted?: () => void;
}

/** 命令宿主端口（Viewer 实现；测试可用假宿主）。 */
export interface SmartDimensionCommandHost {
    /** 解析活动草图上下文；无活动草图返回 null。 */
    readonly resolveContext: () => SmartDimensionSketchContext | null;
    /** 请求一次渲染。 */
    readonly requestRender: () => void;
    /** 命令退出通知（宿主负责切回草图模式与清理选择）。 */
    readonly onExit: () => void;
    /**
     * 提示通知（宿主转给 UI）。
     *
     * 当前用于"尺寸已创建但未驱动几何"这类不能静默的结果。
     */
    readonly onNotice?: (message: string, level: 'info' | 'warning' | 'error') => void;
}

/**
 * 智能尺寸命令控制器（阶段 4 状态机）。
 *
 * 状态迁移（01 基线 §4.1）：
 * collecting → previewing-unlocked ⇄ previewing-locked → placed-pending-value
 * → resolving → (committed｜driven-decision)；committed 为瞬态，连续模式回
 * collecting，单次模式退出。
 *
 * 关键纪律：
 * 1. 选择/预览/锁定不入历史；确认创建 = 一笔 Model 事务（阶段 2 应用服务）；
 * 2. Esc 在选择阶段撤回最后一次选择，空选择时退出命令，不触发文档级 Undo；
 * 3. 尺寸驱动与点/轮廓拖拽均使用统一参数内核，控制器只负责尺寸命令生命周期；
 * 4. 无解/不支持 island 不静默：进入 driven-decision，由用户决定转 driven 或取消。
 */
export class SketchSmartDimensionController {
    /** 宿主端口。 */
    private readonly host: SmartDimensionCommandHost;

    /** 推断注册表（内置策略已注册）。 */
    private readonly inferenceRegistry = new SketchDimensionInferenceRegistry();

    /** 尺寸历史事务端口（宿主注入；未注入时确认创建会显式失败）。 */
    private transactionPort: SketchDimensionHistoryTransactionPort | null = null;

    /** 当前状态。 */
    private state: SmartDimensionCommandState = 'inactive';

    /** 有序选择项。 */
    private picks: SmartDimensionPickItem[] = [];

    /** 当前候选（未锁定时随指针刷新）。 */
    private candidate: SmartDimensionCandidate | null = null;

    /** 锁定的候选；null 表示未锁定。 */
    private lockedCandidate: SmartDimensionCandidate | null = null;

    /** 放置局部坐标。 */
    private placementLocal: SmartDimensionVec2 | null = null;

    /** 放置世界坐标。 */
    private placementWorld: readonly [number, number, number] | null = null;

    /** 过定义/无解决策上下文。 */
    private drivenDecision: SmartDimensionDrivenDecisionContext | null = null;

    /** driven 决策暂存的创建输入。 */
    private pendingDrivenInput: CreateSketchDimensionInput | null = null;

    /** 是否连续命令模式。 */
    private continuous = true;

    /** 固定方向命令（H/V Dimension）；null 表示标准智能尺寸。 */
    private forcedOrientation: 'horizontal' | 'vertical' | null = null;

    /** 是否路径长度命令模式。 */
    private pathLengthMode = false;

    /** 序列编排模式（chain/baseline）；null 表示非序列命令。 */
    private sequenceMode: 'chain' | 'baseline' | 'ordinate' | null = null;

    /**
     * 链尺寸继承方向（首对点连线的单位向量；SW：首对自动生成、方向不可改，
     * 后续每对的测量与标注方向全部继承它）。null = 首对尚未提交。
     */
    private chainDirection: readonly [number, number] | null = null;

    /** 链尺寸分组 ID（本次链命令产出的全部尺寸共享；拖动标注整组共线联动）。 */
    private chainGroupId: string | null = null;

    /**
     * 尺寸链测量轴（SW：0 界线只能沿草图面绝对水平/竖直拉出，界线方向的
     * **垂直轴**即整条链的测量轴）。由基准预览阶段的指针主方向记录；
     * H/V 变体锁死；null = 尚未确定（凑对时按目标点主方向兜底）。
     */
    private ordinateMeasureAxis: 'horizontal' | 'vertical' | null = null;

    /**
     * 尺寸链 0 标注的放置位置（选基准后空白单击锁定；SW——放置 0 的这一击
     * 同时锁定测量轴与引导线位置，此后 0 界线不再跟随鼠标）。
     */
    private ordinateGuidePlacement: SmartDimensionVec2 | null = null;

    /** 序列模式已提交的尺寸计数（baseline 标注堆叠偏移用）。 */
    private sequenceCommittedCount = 0;

    /** 快照监听器。 */
    private readonly listeners = new Set<SmartDimensionSnapshotListener>();

    /**
     * @param host 命令宿主端口。
     */
    public constructor(host: SmartDimensionCommandHost) {
        this.host = host;
        registerBuiltinSmartDimensionInferenceStrategies(this.inferenceRegistry);
    }

    /**
     * 注入尺寸历史事务端口。
     *
     * @param port 事务端口；传 null 清除。
     */
    public configureTransactionPort(port: SketchDimensionHistoryTransactionPort | null): void {
        this.transactionPort = port;
    }

    /**
     * 启动命令。
     *
     * @param options 启动选项（阶段 8：H/V 固定方向、路径长度、chain/baseline 序列共用底座）。
     */
    public start(options?: SmartDimensionStartOptions): void {
        this.resetSession();
        this.continuous = options?.continuous ?? true;
        this.forcedOrientation = options?.forcedOrientation ?? null;
        this.pathLengthMode = options?.pathLength === true;
        this.sequenceMode = options?.sequence ?? null;
        this.chainDirection = null;
        this.chainGroupId = null;
        this.ordinateMeasureAxis = null;
        this.ordinateGuidePlacement = null;
        this.sequenceCommittedCount = 0;
        this.state = 'collecting';
        this.publish();
    }

    /** 命令是否激活。 */
    public isActive(): boolean {
        return this.state !== 'inactive';
    }

    /**
     * 取消并退出命令（不产生历史）。
     */
    public cancelCommand(): void {
        if (this.state === 'inactive') {
            return;
        }
        this.resetSession();
        this.state = 'inactive';
        this.publish();
        this.host.onExit();
    }

    /**
     * 处理实体点击。
     *
     * @param object 命中运行时对象。
     * @param modifiers 选择时的修饰键状态。
     * @returns 是否被命令消费。
     */
    public handleEntityClick(object: THREE.Object3D, modifiers?: SmartDimensionPickModifiers): boolean {
        if (!this.isCollectingLike()) {
            return false;
        }

        const context = this.host.resolveContext();

        if (!context) {
            return false;
        }

        /** 路径长度：点击封闭轮廓 = 自动选中该封闭图形的全部线段边（SW 同款）。 */
        if (this.pathLengthMode && isSketchContour(object)) {
            return this.handlePathLengthContourClick(object);
        }

        const pick = projectSmartDimensionPickItem(object, this.picks.length, modifiers);

        if (!pick) {
            return false;
        }
        // 命令变体的合法拾取类型过滤（阶段 8）：路径长度收路径段（线/弧/样条）；H/V 只收点/线。
        if (this.pathLengthMode && pick.itemKind !== 'line' && pick.itemKind !== 'arc' && pick.itemKind !== 'spline') {
            return true;
        }
        /**
         * 链/基准序列只收**点**（SW 语义：链尺寸是连续点两两成尺寸）。
         * 点到线段/圆上不产生任何尺寸效果——消费点击但不入选。
         */
        if (this.sequenceMode && pick.itemKind !== 'point') {
            return true;
        }
        if (this.forcedOrientation && pick.itemKind !== 'point' && pick.itemKind !== 'line') {
            return true;
        }

        const existingIndex = this.picks.findIndex((item) => item.runtimeId === pick.runtimeId);

        if (existingIndex >= 0) {
            // 再次点击已选对象 = 取消该选择。
            this.picks.splice(existingIndex, 1);
            this.picks = this.picks.map((item, index) => ({...item, selectionOrder: index}));
        } else {
            const nextPicks = [...this.picks, pick];

            // 组合永远无法形成候选时拒绝该选择（路径长度链不走注册表组合判定）。
            if (!this.pathLengthMode && !this.inferenceRegistry.hasPotentialCombination(nextPicks)) {
                return true;
            }
            /**
             * 路径长度即时校验（SW：选择不相邻线段时直接提示且不选中）：
             * 已有选择时，新线段加入后必须仍构成端到端相连的单一链。
             */
            if (this.pathLengthMode && this.picks.length >= 1
                && !resolveSmartDimensionPathLengthCandidate(nextPicks)) {
                this.host.onNotice?.('该线段与已选路径不相连（或形成分叉），未加入选择。', 'warning');

                return true;
            }
            this.picks = nextPicks;
        }

        this.lockedCandidate = null;
        this.refreshCandidate(null);
        this.publish();

        // 序列编排（chain/baseline）：凑齐一对引用即自动提交 driven 尺寸。
        if (this.sequenceMode && this.picks.length === 2) {
            void this.commitSequenceStep();
        }

        return true;
    }

    /**
     * 当前是否处于路径长度命令（选择层据此放行封闭轮廓拾取）。
     *
     * @returns 是否路径长度命令活动中。
     */
    public isPathLengthCommand(): boolean {
        return this.pathLengthMode && this.state !== 'inactive';
    }

    /**
     * 当前是否处于尺寸链命令（宿主据此切换专用预览）。
     *
     * @returns 是否尺寸链命令活动中。
     */
    public isOrdinateCommand(): boolean {
        return this.sequenceMode === 'ordinate' && this.state !== 'inactive';
    }

    /**
     * 尺寸链基准预览点（已选基准、尚未凑对时的"0 界线跟随鼠标"预览锚点）。
     *
     * @returns 基准点局部坐标；非该阶段返回 null。
     */
    public resolveOrdinateDatumPreviewPoint(): SmartDimensionVec2 | null {
        if (this.sequenceMode !== 'ordinate' || this.picks.length !== 1) {
            return null;
        }
        /**
         * 已生成过尺寸后基准 0 预览退场：正式首条尺寸自带基准记号与 0 标注，
         * 预览若继续固定显示在放置位置，用户拖动整组挪走尺寸线后会看到
         * 一套残留在旧位置的"多余基准线"。
         */
        if (this.sequenceCommittedCount > 0) {
            return null;
        }

        return this.picks[0]?.point ?? null;
    }

    /**
     * 把指针位置吸附到尺寸链 0 界线的合法方向上（SW：界线只能沿草图面
     * 绝对水平/竖直拉出，不能自由移动）。
     *
     * 水平尺寸链 → 界线恒竖直；竖直尺寸链 → 界线恒水平；普通尺寸链按
     * 指针相对基准的主方向取轴，同时记录测量轴（= 界线方向的垂直轴）。
     *
     * @param pointerLocal 指针局部坐标。
     * @returns 吸附后的界线端点；非基准预览阶段返回 null。
     */
    public resolveOrdinateGuideSnappedPointer(pointerLocal: SmartDimensionVec2): SmartDimensionVec2 | null {
        const datum = this.resolveOrdinateDatumPreviewPoint();

        if (!datum) {
            return null;
        }

        // 0 已放置：界线固定在放置位置，不再跟随鼠标（SW）。
        if (this.ordinateGuidePlacement) {
            return this.ordinateGuidePlacement;
        }

        const deltaX = pointerLocal[0] - datum[0];
        const deltaY = pointerLocal[1] - datum[1];
        const guideVertical = this.forcedOrientation === 'horizontal'
            ? true
            : this.forcedOrientation === 'vertical'
                ? false
                : Math.abs(deltaY) >= Math.abs(deltaX);

        // 普通尺寸链：界线拉动方向定测量轴（界线竖直 → 沿水平量）。
        if (!this.forcedOrientation) {
            this.ordinateMeasureAxis = guideVertical ? 'horizontal' : 'vertical';
        }

        return guideVertical
            ? [datum[0], pointerLocal[1]]
            : [pointerLocal[0], datum[1]];
    }

    /**
     * 路径长度：封闭轮廓点击展开为整圈线段选择（SW 同款）。
     *
     * @param contour 命中的轮廓面片。
     * @returns 恒为 true（点击已被命令消费）。
     */
    private handlePathLengthContourClick(contour: SketchContour): boolean {
        const source = contour.source;

        if (!isSketchCollectionObject(source)) {
            this.host.onNotice?.('该轮廓不是线段集合，无法作为路径长度的选择对象。', 'warning');

            return true;
        }

        const memberEdges = source.children.filter(
            (child) => isSketchLineObject(child) || isSketchArcObject(child),
        );

        if (memberEdges.length < 2) {
            this.host.onNotice?.('该轮廓可入链的边不足（路径至少需要两段），未加入选择。', 'warning');

            return true;
        }

        const nextPicks = [...this.picks];

        memberEdges.forEach((edge) => {
            if (nextPicks.some((item) => item.runtimeId === edge.uuid)) {
                return;
            }

            const pick = projectSmartDimensionPickItem(edge, nextPicks.length);

            if (pick?.itemKind === 'line' || pick?.itemKind === 'arc') {
                nextPicks.push(pick);
            }
        });

        if (nextPicks.length === this.picks.length) {
            return true;
        }
        // 整圈加入后仍须构成单一连通链（与已选路径不相连时整体拒绝）。
        if (!resolveSmartDimensionPathLengthCandidate(nextPicks)) {
            this.host.onNotice?.('该轮廓与已选路径不相连，未加入选择。', 'warning');

            return true;
        }

        this.picks = nextPicks.map((item, index) => ({...item, selectionOrder: index}));
        this.lockedCandidate = null;
        this.refreshCandidate(null);
        this.publish();

        return true;
    }

    /**
     * 面板移除单个选择项（路径长度属性面板"所选实体"列表的取消按钮）。
     *
     * @param runtimeId 选择项运行时 uuid。
     * @returns 是否实际移除。
     */
    public removePickByRuntimeId(runtimeId: string): boolean {
        const index = this.picks.findIndex((item) => item.runtimeId === runtimeId);

        if (index < 0 || !this.isCollectingLike()) {
            return false;
        }

        this.picks.splice(index, 1);
        this.picks = this.picks.map((item, order) => ({...item, selectionOrder: order}));
        this.lockedCandidate = null;
        this.refreshCandidate(null);
        this.publish();

        return true;
    }

    /**
     * 面板确认路径长度选择（属性面板 ✓）：自动放置并按当前总长落库。
     *
     * SW 语义：确认时选择有错误（断链/分叉/不足两段）就报错；合法则生成
     * 总长尺寸，标注自动放在链包围盒上方（无放置点击）。
     *
     * @returns 是否被命令消费（含报错分支）。
     */
    public async confirmPathLengthSelection(): Promise<boolean> {
        if (!this.pathLengthMode) {
            return false;
        }

        const candidate = this.lockedCandidate ?? this.candidate;

        if (!candidate || candidate.dimensionKind !== 'path-length') {
            if (this.picks.length > 0) {
                this.host.onNotice?.(
                    '所选线段未构成端到端相连的单一路径（存在分叉、断开或不足两段），请调整选择后再确认。',
                    'error',
                );
            }

            return true;
        }

        const context = this.host.resolveContext();

        if (!context) {
            return false;
        }

        /** 自动放置：链包围盒上方 10mm（面板确认没有放置点击）。 */
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        candidate.previewAnchors.forEach((anchor) => {
            minX = Math.min(minX, anchor[0]);
            maxX = Math.max(maxX, anchor[0]);
            maxY = Math.max(maxY, anchor[1]);
        });

        const label: SmartDimensionVec2 = Number.isFinite(minX)
            ? [(minX + maxX) / 2, maxY + 0.01]
            : this.resolveDefaultPointer();
        const world = context.referencePlane.localToWorld(label[0], label[1]);

        this.candidate = candidate;
        this.placementLocal = label;
        this.placementWorld = [world.x, world.y, world.z];
        this.state = 'placed-pending-value';
        this.publish();
        await this.confirmValue(candidate.currentValue);

        return true;
    }

    /**
     * 处理指针移动（预览刷新与方向/象限切换）。
     *
     * @param pointerLocal 指针草图局部坐标。
     */
    public handlePointerMove(pointerLocal: SmartDimensionVec2): void {
        if (this.state !== 'collecting' && this.state !== 'previewing-unlocked' && this.state !== 'previewing-locked') {
            return;
        }
        if (this.state === 'previewing-locked') {
            // 锁定态只调整标注位置，测量类型不随指针变化。
            this.publish();
            return;
        }
        this.refreshCandidate(pointerLocal);
        this.publish();
    }

    /**
     * 处理右键：预览态锁定/解锁候选。
     *
     * 说明：官方仅记载“右键锁定”；“再次右键解锁”为 I 级推断（01 基线 §4.3），
     * 实机金标后如不符只需调整本方法。
     *
     * @returns 是否被命令消费。
     */
    public handleRightClick(): boolean {
        if (this.state === 'previewing-unlocked' && this.candidate) {
            this.lockedCandidate = this.candidate;
            this.state = 'previewing-locked';
            this.publish();

            return true;
        }
        if (this.state === 'previewing-locked') {
            this.lockedCandidate = null;
            this.state = 'previewing-unlocked';
            this.publish();

            return true;
        }

        return false;
    }

    /**
     * 处理空白处点击：放置尺寸并进入待输入值状态。
     *
     * @param pointerLocal 放置局部坐标。
     * @param modifiers 放置时的修饰键（Shift 放置对称角 = 完整对称角，SD-SEL-016）。
     * @returns 是否被命令消费。
     */
    public handlePlacementClick(pointerLocal: SmartDimensionVec2, modifiers?: SmartDimensionPickModifiers): boolean {
        /**
         * 尺寸链放置 0（SW）：选基准后空白单击把 0 标注放置到吸附位置——
         * 这一击同时锁定测量轴与引导线位置，此后 0 界线不再跟随鼠标。
         */
        if (this.sequenceMode === 'ordinate' && this.picks.length === 1 && !this.ordinateGuidePlacement) {
            const snapped = this.resolveOrdinateGuideSnappedPointer(pointerLocal);

            if (snapped) {
                this.ordinateGuidePlacement = snapped;
                this.publish();

                return true;
            }
        }
        /**
         * 路径长度确认时的选择校验（SW：选择有错误就报错）：已选了线段但
         * 构不成端到端相连的单一链（分叉/断链/段数不足）时候选为 null、
         * 状态停在 collecting——此时确认点击必须显式报错而不是静默无响应。
         */
        if (this.pathLengthMode && this.state === 'collecting' && this.picks.length > 0) {
            this.host.onNotice?.(
                '所选线段未构成端到端相连的单一路径（存在分叉、断开或不足两段），请调整选择后再确认。',
                'error',
            );

            return true;
        }
        if (this.state !== 'previewing-unlocked' && this.state !== 'previewing-locked') {
            return false;
        }

        let candidate = this.lockedCandidate ?? this.candidate;
        const context = this.host.resolveContext();

        if (!candidate || !context) {
            return false;
        }

        //------ 对称角 Shift 完整角 开始------
        if (candidate.dimensionKind === 'symmetric-angle' && modifiers?.shiftKey) {
            candidate = {
                ...candidate,
                valueScale: 2,
                currentValue: candidate.currentValue * 2,
                branch: {...candidate.branch, symmetricAngleMode: 'full'},
            };
        }
        //------ 对称角 Shift 完整角 结束------

        const world = context.referencePlane.localToWorld(pointerLocal[0], pointerLocal[1]);

        this.candidate = candidate;
        this.placementLocal = pointerLocal;
        this.placementWorld = [world.x, world.y, world.z];
        this.state = 'placed-pending-value';
        this.publish();

        return true;
    }

    /**
     * 处理 Esc。
     *
     * 语义（01 基线 §4.2）：选择阶段撤回最后一次选择；空选择退出命令；
     * 待输入值阶段回退到预览（Modify 打开时连续 Esc 的完整矩阵为 V 级待实机）。
     */
    public handleEscape(): void {
        if (this.state === 'inactive') {
            return;
        }
        /**
         * Esc = 确认并退出（用户金标：所有智能尺寸类型统一）。
         * 已落库的尺寸保留，未完成的选择/放置/待输入状态全部丢弃——
         * 曾经的"逐级撤回上一次选择"语义被否决（尺寸链里表现为：选完点按
         * Esc 结束不了命令，反而回到基准 0 界线跟随状态）。
         */
        this.cancelCommand();
    }

    /**
     * 确认放置尺寸的数值（Modify 确认）。
     *
     * @param nominalValue 名义值（核心单位：米/弧度）。
     * @throws 当控制器不在待输入值状态或缺少待创建候选尺寸时抛出错误。
     * @throws 当无法解析活动草图上下文时抛出错误。
     */
    public async confirmValue(nominalValue: number): Promise<void> {
        if (this.state !== 'placed-pending-value' || !this.candidate) {
            throw new Error('当前不在待输入值状态，无法确认尺寸值。');
        }

        const context = this.host.resolveContext();

        if (!context) {
            throw new Error('没有活动草图，无法创建尺寸。');
        }

        const candidate = this.candidate;
        const input: CreateSketchDimensionInput = {
            sketchId: context.sketchId,
            dimensionKind: candidate.dimensionKind,
            role: 'driving',
            nominalValue,
            references: candidate.references,
            branch: candidate.branch,
            display: this.placementLocal ? {labelPosition: this.placementLocal} : {},
            valueScale: candidate.valueScale,
        };

        this.state = 'resolving';
        this.publish();

        // 预演求解：driving 尺寸加入 island 后是否可解（阶段 3 尺寸驱动入口）。
        const rehearsal = this.rehearseDrivingSolve(context, input);

        if (rehearsal.kind === 'blocked') {
            this.drivenDecision = rehearsal.decision;
            this.pendingDrivenInput = input;
            this.state = 'driven-decision';
            this.publish();
            return;
        }

        /**
         * island 含参数内核暂不支持的关系/尺寸、无法驱动时：按 SW 语义**自动转
         * 从动**落库（值取当前几何量）并提示。曾以 driving 落库+警告——留下
         * "驱动但未生效"的病态尺寸，之后每次改它或同岛其它尺寸的值都会弹
         * "暂不支持参数驱动"警告。
         */
        if (rehearsal.undrivenReason) {
            await this.commitCreate(context, {
                ...input,
                role: 'driven',
                nominalValue: candidate.currentValue,
                measuredValue: candidate.currentValue,
            }, null);
            this.host.onNotice?.('该尺寸暂不支持参数驱动，已自动设为从动。', 'warning');
            return;
        }
        await this.commitCreate(context, input, rehearsal.session);
    }

    /**
     * driven 决策：转为 driven（参考）尺寸创建。
     * @throws 当控制器没有等待决策的尺寸输入或候选尺寸时抛出错误。
     * @throws 当无法解析活动草图上下文时抛出错误。
     */
    public async convertPendingToDriven(): Promise<void> {
        if (this.state !== 'driven-decision' || !this.pendingDrivenInput || !this.candidate) {
            throw new Error('当前没有等待决策的尺寸。');
        }

        const context = this.host.resolveContext();

        if (!context) {
            throw new Error('没有活动草图，无法创建尺寸。');
        }

        /**
         * driven 尺寸只测量不驱动：名义值与测量值都取当前几何量
         * （用户输入的无解值不落盘，对齐 SOLIDWORKS 参考尺寸显示语义）。
         */
        const input: CreateSketchDimensionInput = {
            ...this.pendingDrivenInput,
            role: 'driven',
            nominalValue: this.candidate.currentValue,
            measuredValue: this.candidate.currentValue,
        };

        await this.commitCreate(context, input, null);
    }

    /**
     * driven 决策/待输入：取消当前尺寸创建（不产生历史）。
     */
    public cancelPendingDimension(): void {
        this.drivenDecision = null;
        this.pendingDrivenInput = null;
        this.placementLocal = null;
        this.placementWorld = null;
        if (this.state === 'driven-decision' || this.state === 'placed-pending-value' || this.state === 'resolving') {
            this.state = this.picks.length > 0 ? 'previewing-unlocked' : 'collecting';
            this.refreshCandidate(null);
        }
        this.publish();
    }

    /**
     * 读取当前快照。
     *
     * @returns 命令纯数据快照。
     */
    public getSnapshot(): SmartDimensionCommandSnapshot {
        return {
            state: this.state,
            picks: this.picks.map((pick) => ({
                itemKind: pick.itemKind,
                entityId: pick.entityId,
                ...(pick.pointId ? {pointId: pick.pointId} : {}),
                runtimeId: pick.runtimeId,
            })),
            candidate: this.lockedCandidate ?? this.candidate,
            placementWorld: this.placementWorld,
            drivenDecision: this.drivenDecision,
            continuous: this.continuous,
        };
    }

    /**
     * 订阅快照变化。
     *
     * @param listener 监听器。
     * @returns 取消订阅函数。
     */
    public observe(listener: SmartDimensionSnapshotListener): () => void {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    /** 释放（宿主 dispose 链调用）。 */
    public dispose(): void {
        this.resetSession();
        this.state = 'inactive';
        this.listeners.clear();
    }

    /** 当前是否处于可继续收集选择的状态。 */
    private isCollectingLike(): boolean {
        return this.state === 'collecting'
            || this.state === 'previewing-unlocked'
            || this.state === 'previewing-locked';
    }

    /** 重算候选并同步状态（collecting ⇄ previewing-unlocked）。 */
    private refreshCandidate(pointerLocal: SmartDimensionVec2 | null): void {
        const fallbackPointer: SmartDimensionVec2 = pointerLocal
            ?? this.placementLocal
            ?? this.resolveSequenceAlignedPointer()
            ?? this.resolveDefaultPointer();

        if (this.pathLengthMode) {
            // 路径长度命令：不走推断注册表，链校验通过即形成 path-length 候选。
            this.candidate = resolveSmartDimensionPathLengthCandidate(this.picks);
        } else {
            const inferred = this.inferenceRegistry.resolve({picks: this.picks, pointer: fallbackPointer});

            // H/V 固定方向命令：候选强制水平/竖直距离语义（不修改推断器本身）。
            this.candidate = this.forcedOrientation && inferred
                ? applySmartDimensionForcedOrientation(inferred, this.forcedOrientation)
                : inferred;
        }
        if (this.isCollectingLike()) {
            this.state = this.candidate ? 'previewing-unlocked' : 'collecting';
        }
    }

    /**
     * 序列编排（chain/baseline）提交一步：当前一对引用的候选自动创建尺寸。
     *
     * 语义（01 §3.2 + SW 金标）：Chain 草图内 driven-only；Baseline 共享首基准、
     * **默认 driving**（值锁定当前几何量），过定义/无解时自动转 driven 并提示
     * （对齐 SW"冗余尺寸设为从动"，不打断连续加点流）。失败时撤回本次新引用。
     */
    private async commitSequenceStep(): Promise<void> {
        const sequence = this.sequenceMode;
        const candidate = this.candidate;
        const context = this.host.resolveContext();

        if (!sequence || this.picks.length !== 2 || !context) {
            return;
        }

        /**
         * 序列编排没有指针交互：两点距离候选的方向域不应由缺省指针位置决定，
         * 统一归一为 aligned（直线距离）；H/V 序列变体待实机金标（V 注记）。
         */
        const normalized = candidate?.dimensionKind === 'point-distance' && candidate.orientation !== 'aligned'
            ? (() => {
                const first = candidate.previewAnchors[0];
                const second = candidate.previewAnchors[1];

                if (!first || !second) {
                    return candidate;
                }

                return {
                    ...candidate,
                    orientation: 'aligned' as const,
                    currentValue: Math.hypot(second[0] - first[0], second[1] - first[1]),
                    branch: {orientation: 'aligned' as const},
                };
            })()
            : candidate;

        // 组合无候选或几何量退化（两引用重合）：撤回第二个引用，保留基准/前项。
        if (!normalized || normalized.currentValue <= 1e-12) {
            this.picks = this.picks.slice(0, 1);
            this.refreshCandidate(null);
            this.publish();
            return;
        }

        //------ 链尺寸方向继承（SW 语义） 开始------
        /**
         * 首对点：方向 = 两点连线单位向量，标注自动放在连线**左侧**、不可改；
         * 后续每对：测量值 = 沿继承方向的投影，尺寸线与标注方向全部继承首对。
         * 不继承的话每对各按自己连线摆——就是"四面八方"。
         */
        let sequenceValue = normalized.currentValue;
        let sequenceBranch = normalized.branch;
        let sequenceLabel = resolveSmartDimensionSequenceLabel(normalized, this.sequenceCommittedCount);

        if (sequence === 'chain' && normalized.dimensionKind === 'point-distance') {
            const first = normalized.previewAnchors[0];
            const second = normalized.previewAnchors[1];

            if (first && second) {
                const deltaX = second[0] - first[0];
                const deltaY = second[1] - first[1];

                if (!this.chainDirection) {
                    const span = Math.hypot(deltaX, deltaY);

                    this.chainDirection = [deltaX / span, deltaY / span];
                }

                const direction = this.chainDirection;
                const projected = Math.abs(deltaX * direction[0] + deltaY * direction[1]);

                /**
                 * 本段在继承方向上投影退化（与链方向垂直）：该段不产尺寸，
                 * 但链**以新点续接**（对齐 SW：垂直段跳过，下一段从新点继续量）。
                 */
                if (projected <= 1e-12) {
                    this.picks = [{...this.picks[1]!, selectionOrder: 0}];
                    this.refreshCandidate(null);
                    this.publish();
                    return;
                }

                /**
                 * 共享轴（SW：整条链尺寸线共线，摆在图形左侧不与几何重叠）：
                 * 优先读**组内已有尺寸的当前标注位置**——用户在链命令进行中拖动过
                 * 标注（整组联动挪轴）时，后续新段必须跟着落在挪后的轴上；
                 * 组内还没有尺寸（首段）才按草图几何算初始避让轴。
                 * 曾经首段算定后一直用缓存值，用户挪走标注后新段仍落在旧轴上。
                 */
                const normal: readonly [number, number] = [-direction[1], direction[0]];

                if (!this.chainGroupId) {
                    this.chainGroupId = THREE.MathUtils.generateUUID();
                }

                const axisOffset = this.resolveChainAxisOffsetFromExistingLabels(context, normal)
                    ?? this.resolveChainAxisOffset(context, normal, [first, second]);

                sequenceValue = projected;
                sequenceBranch = {...normalized.branch, fixedDirection: direction, chainGroupId: this.chainGroupId};

                /** 标注 = 段中点投到共享轴（沿链方向留在自己段上，法向对齐整链共线）。 */
                const middle: readonly [number, number] = [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
                const middleOffset = middle[0] * normal[0] + middle[1] * normal[1];

                sequenceLabel = [
                    middle[0] + normal[0] * (axisOffset - middleOffset),
                    middle[1] + normal[1] * (axisOffset - middleOffset),
                ];
            }
        }
        //------ 链尺寸方向继承 结束------

        //------ 尺寸链（SW Ordinate 坐标尺寸） 开始------
        /**
         * 尺寸链：首点是共享**基准**，后续每点生成"相对基准沿测量方向的坐标"
         * 的从动尺寸。方向：水平/竖直尺寸链锁轴；普通尺寸链继承首对连线方向。
         * 标注放在引导线（过基准点、沿测量方向）上目标点的投影处——布局器按
         * branch.ordinate 画"引导线 + 界线 + 单向箭头"样式。
         */
        if (sequence === 'ordinate' && normalized.dimensionKind === 'point-distance') {
            const datum = normalized.previewAnchors[0];
            const target = normalized.previewAnchors[1];

            if (datum && target) {
                const deltaX = target[0] - datum[0];
                const deltaY = target[1] - datum[1];

                /**
                 * 测量轴恒为草图面绝对水平/竖直（SW：0 界线只能沿 H/V 拉出，
                 * 界线方向的垂直轴即测量轴）。普通尺寸链用基准预览阶段记录的
                 * 轴；无记录（测试直点/无指针）按首个目标点主方向兜底并锁定。
                 */
                const measureAxis: 'horizontal' | 'vertical' = this.forcedOrientation
                    ?? this.ordinateMeasureAxis
                    ?? (Math.abs(deltaX) >= Math.abs(deltaY) ? 'horizontal' : 'vertical');

                if (!this.forcedOrientation) {
                    this.ordinateMeasureAxis = measureAxis;
                }

                const direction: readonly [number, number] = measureAxis === 'horizontal' ? [1, 0] : [0, 1];
                const signedProjection = deltaX * direction[0] + deltaY * direction[1];

                // 坐标差为零（与测量方向垂直）：值无法落库，撤回该点并提示。
                if (Math.abs(signedProjection) <= 1e-12) {
                    this.picks = this.picks.slice(0, 1);
                    this.host.onNotice?.('该点与基准在测量方向上的坐标差为零，未生成尺寸。', 'warning');
                    this.refreshCandidate(null);
                    this.publish();
                    return;
                }

                if (!this.chainGroupId) {
                    this.chainGroupId = THREE.MathUtils.generateUUID();
                }

                sequenceValue = Math.abs(signedProjection);
                sequenceBranch = {
                    ...normalized.branch,
                    // 测量轴恒为绝对 H/V（SW），统一以 orientation 表达。
                    orientation: measureAxis,
                    ordinate: true,
                    ...(this.sequenceCommittedCount === 0 ? {ordinateBaseline: true} : {}),
                    chainGroupId: this.chainGroupId,
                };
                /**
                 * 标注 = 引导线上目标点的投影；引导线法向位置跟随**组内已有标注**
                 * （首条过基准点；用户拖动过 0 标注后，后续新条落在挪后的同一条
                 * 引导线上——否则"三件套"与 0 不在同一水平线）。
                 */
                const ordinateNormal: readonly [number, number] = [-direction[1], direction[0]];
                const existingOffset = this.resolveChainAxisOffsetFromExistingLabels(context, ordinateNormal);
                const datumOffset = datum[0] * ordinateNormal[0] + datum[1] * ordinateNormal[1];
                /** 引导线法向位置：组内已有标注 > 放置的 0 位置 > 过基准点。 */
                const placementOffset = this.ordinateGuidePlacement
                    ? this.ordinateGuidePlacement[0] * ordinateNormal[0]
                        + this.ordinateGuidePlacement[1] * ordinateNormal[1]
                    : null;
                const guideDelta = existingOffset !== null
                    ? existingOffset - datumOffset
                    : placementOffset !== null
                        ? placementOffset - datumOffset
                        : 0;

                sequenceLabel = [
                    datum[0] + direction[0] * signedProjection + ordinateNormal[0] * guideDelta,
                    datum[1] + direction[1] * signedProjection + ordinateNormal[1] * guideDelta,
                ];
            }
        }
        //------ 尺寸链 结束------

        /** 序列尺寸角色：chain/ordinate 恒 driven；baseline 默认 driving（SW 基准尺寸语义）。 */
        const sequenceRole = sequence === 'baseline' ? 'driving' : 'driven';
        const input: CreateSketchDimensionInput = {
            sketchId: context.sketchId,
            dimensionKind: normalized.dimensionKind,
            role: sequenceRole,
            nominalValue: sequenceValue,
            ...(sequenceRole === 'driven' ? {measuredValue: sequenceValue} : {}),
            references: normalized.references,
            branch: sequenceBranch,
            display: {labelPosition: sequenceLabel},
            valueScale: normalized.valueScale,
        };

        try {
            if (sequenceRole === 'driving') {
                /**
                 * driving 需先预演求解（与标准创建同一入口）：名义值就是当前几何量，
                 * 可解时几何不动、关系落库锁定该量；过定义/无解时**自动转 driven**
                 * 并提示——序列是连续加点流，不进 driven-decision 弹窗打断。
                 */
                const rehearsal = this.rehearseDrivingSolve(context, input);

                if (rehearsal.kind === 'blocked') {
                    await this.commitCreate(context, {...input, role: 'driven', measuredValue: sequenceValue}, null);
                    this.host.onNotice?.(rehearsal.decision.message ?? '几何已完全约束，基准尺寸已设为从动。', 'warning');
                } else {
                    await this.commitCreate(context, input, rehearsal.session);
                    if (rehearsal.undrivenReason) {
                        this.host.onNotice?.(rehearsal.undrivenReason, 'warning');
                    }
                }
            } else {
                await this.commitCreate(context, input, null);
            }
            this.sequenceCommittedCount += 1;
        } catch (error) {
            // 创建失败（校验/端口缺失）：撤回第二个引用，保持命令继续可用。
            console.warn('[SmartDimension] 序列尺寸创建失败：', error);
            this.picks = this.picks.slice(0, 1);
            this.state = 'collecting';
            this.refreshCandidate(null);
            this.publish();
        }
    }

    /**
     * 从组内已有尺寸的当前标注位置读取共享轴偏移。
     *
     * 整组标注始终共线（拖动联动保证），任一成员的标注在法向上的投影
     * 就是当前轴——用户拖动过标注后，新段据此落在挪后的轴上。
     *
     * @param context 活动草图上下文。
     * @param normal 链左法向（单位向量）。
     * @returns 当前轴偏移；组内还没有带标注位置的尺寸时返回 null。
     */
    private resolveChainAxisOffsetFromExistingLabels(
        context: SmartDimensionSketchContext,
        normal: readonly [number, number],
    ): number | null {
        if (!this.chainGroupId) {
            return null;
        }

        for (const dimension of context.repository.listBySketch(context.sketchId)) {
            const snapshot = dimension.toSnapshot();

            if (snapshot.branch.chainGroupId !== this.chainGroupId) {
                continue;
            }

            const label = snapshot.display.labelPosition;

            if (label) {
                return label[0] * normal[0] + label[1] * normal[1];
            }
        }

        return null;
    }

    /**
     * 计算链尺寸共享轴在链左法向上的偏移。
     *
     * 动态避让：取草图**全部运行时点**在法向上的最大投影再加固定边距，
     * 保证整条链的尺寸线落在几何左侧、不与图形重叠（SW 同款摆放）。
     *
     * @param context 活动草图上下文。
     * @param normal 链左法向（单位向量）。
     * @param fallbackAnchors 运行时点不可用时的兜底锚点（首对两点）。
     * @returns 轴偏移（草图局部，米）。
     */
    private resolveChainAxisOffset(
        context: SmartDimensionSketchContext,
        normal: readonly [number, number],
        fallbackAnchors: readonly (readonly [number, number])[],
    ): number {
        /** 尺寸线与几何的固定间距（米）。 */
        const margin = 0.01;
        let maxProjection = Number.NEGATIVE_INFINITY;

        context.collectRuntimePoints().forEach((point) => {
            const local = context.referencePlane.worldToLocal(point.getPosition());

            maxProjection = Math.max(maxProjection, local.x * normal[0] + local.y * normal[1]);
        });
        fallbackAnchors.forEach((anchor) => {
            maxProjection = Math.max(maxProjection, anchor[0] * normal[0] + anchor[1] * normal[1]);
        });

        return maxProjection + margin;
    }

    /** 无指针输入时的兜底指针位置（首个选择项锚点附近）。 */
    private resolveDefaultPointer(): SmartDimensionVec2 {
        const firstPick = this.picks[0];
        const anchor = firstPick?.point ?? firstPick?.center ?? firstPick?.lineStart;

        return anchor ?? [0, 0];
    }

    /**
     * 序列编排（chain/baseline）的等价 aligned 指针。
     *
     * 序列没有指针交互，两点组合的方向域不能由缺省指针位置随机决定：
     * 非轴对齐连线给切向偏移指针（落入 aligned 锥区），轴对齐连线给法向
     * 偏移指针（单轴候选值与 aligned 等价）。H/V 序列变体待实机金标。
     */
    private resolveSequenceAlignedPointer(): SmartDimensionVec2 | null {
        if (!this.sequenceMode || this.picks.length !== 2) {
            return null;
        }

        const readAnchor = (pick: SmartDimensionPickItem): SmartDimensionVec2 | null =>
            pick.point ?? pick.center ?? pick.lineStart ?? null;
        const first = readAnchor(this.picks[0]!);
        const second = readAnchor(this.picks[1]!);

        if (!first || !second) {
            return null;
        }

        const dx = second[0] - first[0];
        const dy = second[1] - first[1];
        const length = Math.hypot(dx, dy);

        if (length < 1e-12) {
            return null;
        }

        const middle: SmartDimensionVec2 = [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
        const axisAligned = Math.abs(dx) < 1e-12 || Math.abs(dy) < 1e-12;

        return axisAligned
            ? [middle[0] - dy, middle[1] + dx]
            : [middle[0] + dx, middle[1] + dy];
    }

    /** 清空一次命令会话的全部临时状态。 */
    private resetSession(): void {
        this.picks = [];
        this.candidate = null;
        this.lockedCandidate = null;
        this.placementLocal = null;
        this.placementWorld = null;
        this.drivenDecision = null;
        this.pendingDrivenInput = null;
    }

    /** 预演 driving 求解结果（复用创建/编辑共享预演，picks 提供优先别名）。 */
    private rehearseDrivingSolve(
        context: SmartDimensionSketchContext,
        input: CreateSketchDimensionInput,
    ):
        | {
            readonly kind: 'ready';
            readonly session: SketchDimensionSolverSession | null;
            /** 尺寸未驱动几何的原因（未支持关系/尺寸时给出，调用方必须提示用户）。 */
            readonly undrivenReason?: string;
        }
        | {readonly kind: 'blocked'; readonly decision: SmartDimensionDrivenDecisionContext} {
        const quantityKind = resolveSketchDimensionQuantityKind(input.dimensionKind);
        const rehearsalDimension: SketchDimensionSnapshot = {
            dimensionId: 'cad:sketch-dimension:rehearsal',
            sketchId: input.sketchId,
            name: '__REHEARSAL__',
            dimensionKind: input.dimensionKind,
            space: 'sketch-2d',
            role: 'driving',
            suppressed: false,
            readOnly: false,
            parameter: {
                parameterId: 'cad:sketch-dimension:rehearsal#param',
                nominalValue: input.nominalValue,
                quantityKind,
                valueScale: input.valueScale ?? 1,
                expression: null,
            },
            measuredValue: null,
            references: input.references,
            branch: input.branch ?? {},
            display: {},
            solveState: 'unknown',
            repair: null,
        };
        const existingDimensions = context.repository
            .listBySketch(input.sketchId)
            .map((dimension) => dimension.toSnapshot());
        /**
         * 用户本次点击对象作为同类型优先别名（副本歧义时以实际点击对象为准）。
         * 点拾取的 `entityId` 只是拓扑宿主，当前 `runtimeId` 仍属于点；禁止用它
         * 覆盖宿主实体别名，否则已有宿主尺寸会把点当成线并被误判为不支持求解。
         */
        const priorityAliases = new Map<string, string>();

        this.picks.forEach((pick) => {
            const aliasKey = pick.pointId ?? pick.entityId;

            if (aliasKey && aliasKey !== pick.runtimeId) {
                priorityAliases.set(aliasKey, pick.runtimeId);
            }
        });

        const rehearsal = rehearseSketchDimensionDrivingSolve({
            context,
            dimensions: [...existingDimensions, rehearsalDimension],
            priorityAliases,
            // 创建路径启用过定义检测（冗余尺寸提示设为从动，对齐 SOLIDWORKS）。
            newDimensionId: rehearsalDimension.dimensionId,
            requestRender: () => this.host.requestRender(),
        });

        if (rehearsal.kind === 'blocked') {
            return {
                kind: 'blocked',
                decision: {reason: rehearsal.reason, message: rehearsal.message},
            };
        }

        return rehearsal;
    }

    /** 执行创建提交并按连续模式收尾。 */
    private async commitCreate(
        context: SmartDimensionSketchContext,
        input: CreateSketchDimensionInput,
        session: SketchDimensionSolverSession | null,
    ): Promise<void> {
        if (!this.transactionPort) {
            // 未注入端口不得让状态卡在 resolving：复位回待输入态供用户取消或重试。
            this.state = 'placed-pending-value';
            this.publish();
            throw new Error('尺寸历史事务端口未注入，无法提交创建。');
        }

        const service = new SketchDimensionApplicationService({
            repository: context.repository,
            identityService: new SketchDimensionIdentityService(),
            validationService: new SketchDimensionValidationService(),
            transactionPort: this.transactionPort,
            ...(session
                ? {
                    solverSessionPort: {
                        begin: () => session,
                    },
                }
                : {}),
        });

        try {
            await service.createDimension(input);
        } catch (error) {
            // 创建失败：应用服务已回滚仓库与 solver 会话，回到决策前状态供用户重试/取消。
            this.state = 'placed-pending-value';
            this.publish();
            throw error;
        }

        // driving 创建会当场驱动几何；补一笔定义提交让下游特征实时重建。
        if (session) {
            context.notifyGeometryCommitted?.();
        }

        this.drivenDecision = null;
        this.pendingDrivenInput = null;
        this.placementLocal = null;
        this.placementWorld = null;
        /**
         * 序列编排提交后保留续接引用：chain 保留后一项作为下一段起点，
         * baseline 保留首项作为共享基准；标准命令清空全部选择。
         */
        if (this.sequenceMode && this.picks.length === 2) {
            const kept = this.sequenceMode === 'chain' ? this.picks[1]! : this.picks[0]!;

            this.picks = [{...kept, selectionOrder: 0}];
        } else {
            this.picks = [];
        }
        this.candidate = null;
        this.lockedCandidate = null;

        if (this.continuous) {
            this.state = 'collecting';
            this.publish();
            return;
        }

        this.state = 'inactive';
        this.publish();
        this.host.onExit();
    }

    /** 广播快照。 */
    private publish(): void {
        const snapshot = this.getSnapshot();

        this.listeners.forEach((listener) => {
            listener(snapshot);
        });
    }
}

/**
 * 把运行时草图对象投影为纯数据选择项。
 *
 * @param object 运行时对象。
 * @param selectionOrder 选择顺序。
 * @param modifiers 选择时的修饰键状态。
 * @returns 选择项；不支持的对象返回 null。
 */
export function projectSmartDimensionPickItem(
    object: THREE.Object3D,
    selectionOrder: number,
    modifiers?: SmartDimensionPickModifiers,
): SmartDimensionPickItem | null {
    const modifierFields = {
        ...(modifiers?.shiftKey ? {shiftKey: true} : {}),
        ...(modifiers?.ctrlKey ? {ctrlKey: true} : {}),
    };

    if (isSketchPointObject(object)) {
        const local = object.getLocalPosition2D();
        const hostEdge = object.getConnectedEdgesSnapshot()[0] ?? null;

        return {
            itemKind: 'point',
            entityId: (hostEdge?.persistentId ?? object.persistentId),
            pointId: object.persistentId,
            // 线中点直接可点选（Select Midpoint 的领域语义；右键菜单命令交互待实机矩阵）。
            topologyRole: (object as {isMidPoint?: boolean}).isMidPoint ? 'midpoint' : 'point',
            runtimeId: object.uuid,
            selectionOrder,
            point: [local.x, local.y],
            ...modifierFields,
        };
    }

    if (isSketchLineObject(object)) {
        const start = object.startPoint.getLocalPosition2D();
        const end = object.endPoint.getLocalPosition2D();

        return {
            itemKind: 'line',
            entityId: object.persistentId,
            runtimeId: object.uuid,
            selectionOrder,
            lineStart: [start.x, start.y],
            lineEnd: [end.x, end.y],
            ...(object.isCenterline ? {isCenterline: true} : {}),
            ...modifierFields,
        };
    }

    if (isSketchCircleObject(object)) {
        const centerPoint = object.getCenterControlPoint();
        const centerLocal = centerPoint.getLocalPosition2D();

        return {
            itemKind: 'circle',
            entityId: object.persistentId,
            runtimeId: object.uuid,
            selectionOrder,
            center: [centerLocal.x, centerLocal.y],
            radius: object.radius,
            centerPointId: centerPoint.persistentId,
            ...modifierFields,
        };
    }

    //------ 样条总长选择 开始------
    if (isSketchSplineObject(object) || isSketchStyleSplineObject(object)) {
        const curve = object.getCurvePoints()
            .map((point) => object.referencePlane.worldToLocal(point.clone()))
            .map((local): SmartDimensionVec2 => [local.x, local.y]);

        if (curve.length < 2) {
            return null;
        }

        let length = 0;

        for (let index = 1; index < curve.length; index += 1) {
            length += Math.hypot(curve[index]![0] - curve[index - 1]![0], curve[index]![1] - curve[index - 1]![1]);
        }

        return {
            itemKind: 'spline',
            entityId: object.persistentId,
            runtimeId: object.uuid,
            selectionOrder,
            splineLength: length,
            splineCurve: curve,
            ...modifierFields,
        };
    }
    //------ 样条总长选择 结束------

    if (isSketchArcObject(object)) {
        const centerPoint = object.getCenterPoint();
        const centerLocal = centerPoint.getLocalPosition2D();
        const definition = object.getArcDefinition();
        const startLocal = object.getStartPoint().getLocalPosition2D();
        const endLocal = object.getEndPoint().getLocalPosition2D();

        return {
            itemKind: 'arc',
            entityId: object.persistentId,
            runtimeId: object.uuid,
            selectionOrder,
            center: [centerLocal.x, centerLocal.y],
            radius: definition.radius,
            centerPointId: centerPoint.persistentId,
            arcStart: [startLocal.x, startLocal.y],
            arcEnd: [endLocal.x, endLocal.y],
            // 弧长 = 半径 × |扫角|（路径长度链的段长）。
            arcLength: definition.radius * Math.abs(definition.endAngle - definition.startAngle),
            ...(definition.clockwise ? {clockwise: true} : {}),
            ...modifierFields,
        };
    }

    return null;
}

/**
 * 对候选应用固定方向语义（Horizontal/Vertical Dimension，阶段 8）。
 *
 * 只对线性距离类候选生效（两点距离/单线长度）：branch.orientation 强制为
 * 命令方向、当前值按锚点投影重算；其余候选类型在固定方向命令下无意义，返回 null。
 *
 * @param candidate 推断器输出的候选（推断语义不变）。
 * @param orientation 命令固定方向。
 * @returns 覆盖后的候选；不适用/投影退化返回 null。
 */
export function applySmartDimensionForcedOrientation(
    candidate: SmartDimensionCandidate,
    orientation: 'horizontal' | 'vertical',
): SmartDimensionCandidate | null {
    if (candidate.dimensionKind !== 'point-distance' && candidate.dimensionKind !== 'length') {
        return null;
    }

    const first = candidate.previewAnchors[0];
    const second = candidate.previewAnchors[1];

    if (!first || !second) {
        return null;
    }

    const projected = orientation === 'horizontal'
        ? Math.abs(second[0] - first[0])
        : Math.abs(second[1] - first[1]);

    if (projected <= 1e-12) {
        return null;
    }

    return {
        ...candidate,
        orientation,
        currentValue: projected,
        branch: {...candidate.branch, orientation},
    };
}

/** 路径链端点匹配容差（米）。 */
const PATH_CHAIN_ENDPOINT_TOLERANCE = 1e-9;

/**
 * 把选择项解析为路径段（端点对 + 段长）。
 *
 * 路径长度链的段抽象：线段=两端点+直线长；弧=两端点+弧长；样条=采样折线
 * 首尾+采样总长。链合法性校验与总长累加都基于这份统一抽象——新增可入链的
 * 曲线类型只需在此登记。
 *
 * @param pick 选择项。
 * @returns 路径段；该选择项不能作为路径段时返回 null。
 */
function resolvePathSegmentOfPick(
    pick: SmartDimensionPickItem,
): {readonly a: SmartDimensionVec2; readonly b: SmartDimensionVec2; readonly length: number} | null {
    if (pick.itemKind === 'line' && pick.lineStart && pick.lineEnd) {
        return {
            a: pick.lineStart,
            b: pick.lineEnd,
            length: Math.hypot(pick.lineEnd[0] - pick.lineStart[0], pick.lineEnd[1] - pick.lineStart[1]),
        };
    }
    if (pick.itemKind === 'arc' && pick.arcStart && pick.arcEnd && pick.arcLength !== undefined) {
        return {a: pick.arcStart, b: pick.arcEnd, length: pick.arcLength};
    }
    if (pick.itemKind === 'spline' && pick.splineCurve && pick.splineCurve.length >= 2
        && pick.splineLength !== undefined) {
        return {
            a: pick.splineCurve[0]!,
            b: pick.splineCurve[pick.splineCurve.length - 1]!,
            length: pick.splineLength,
        };
    }

    return null;
}

/**
 * 从选择集解析路径长度候选（Path Length，阶段 8）。
 *
 * 链合法性（01 §3.2"端到端重合的单一链"）：
 * 1. 全部为路径段（线段/圆弧/样条）且 ≥2 段；
 * 2. 端点度数 ≤2（无分叉）；度数=1 的端点数为 0（闭链）或 2（开链）；
 * 3. 单连通（并查集判定，无孤立子链）。
 *
 * @param picks 有序选择项。
 * @returns path-length 候选；链不合法或段数不足返回 null。
 */
export function resolveSmartDimensionPathLengthCandidate(
    picks: readonly SmartDimensionPickItem[],
): SmartDimensionCandidate | null {
    if (picks.length < 2) {
        return null;
    }

    const segments = picks.map((pick) => resolvePathSegmentOfPick(pick));

    if (segments.some((segment) => segment === null)) {
        return null;
    }

    /** 端点坐标 → 规范键（容差网格）。 */
    const endpointKey = (point: SmartDimensionVec2): string => {
        const grid = PATH_CHAIN_ENDPOINT_TOLERANCE * 10;

        return `${Math.round(point[0] / grid)},${Math.round(point[1] / grid)}`;
    };
    const endpointDegree = new Map<string, number>();
    const endpointGroup = new Map<string, number>();
    const groupParent: number[] = [];
    const findGroup = (index: number): number => {
        let root = index;

        while (groupParent[root] !== root) {
            root = groupParent[root]!;
        }

        return root;
    };
    let totalLength = 0;

    for (let index = 0; index < segments.length; index += 1) {
        const segment = segments[index]!;

        if (segment.length <= PATH_CHAIN_ENDPOINT_TOLERANCE) {
            return null;
        }
        totalLength += segment.length;
        groupParent.push(index);

        [segment.a, segment.b].forEach((point) => {
            const key = endpointKey(point);

            endpointDegree.set(key, (endpointDegree.get(key) ?? 0) + 1);

            const existingGroup = endpointGroup.get(key);

            if (existingGroup === undefined) {
                endpointGroup.set(key, index);
            } else {
                // 并查集合并：共享端点的段属于同一连通链。
                groupParent[findGroup(existingGroup)] = findGroup(index);
            }
        });
    }

    /** 无分叉：任何端点最多被两段共享。 */
    for (const degree of endpointDegree.values()) {
        if (degree > 2) {
            return null;
        }
    }

    /** 开链恰有 2 个自由端点，闭链没有；其余（如两条不相连的线）不合法。 */
    const freeEndpointCount = [...endpointDegree.values()].filter((degree) => degree === 1).length;

    if (freeEndpointCount !== 0 && freeEndpointCount !== 2) {
        return null;
    }

    /** 单连通：全部段归于同一并查集根。 */
    const rootGroup = findGroup(0);

    for (let index = 1; index < segments.length; index += 1) {
        if (findGroup(index) !== rootGroup) {
            return null;
        }
    }

    return {
        dimensionKind: 'path-length',
        currentValue: totalLength,
        valueScale: 1,
        references: picks.map((pick, index) => ({
            entityId: pick.entityId,
            topologyRole: 'entity' as const,
            selectionOrder: index,
        })),
        branch: {},
        // 预览锚点约定：各段 [端A, 端B] 依次平铺（曲线段以端点连线近似预览引线）。
        previewAnchors: segments.flatMap((segment) => [segment!.a, segment!.b]),
    };
}

/**
 * 解析序列编排尺寸的自动标注位置（chain/baseline 无放置点击）。
 *
 * 规则：两锚点中点沿垂直方向偏移；偏移量取锚距的 0.35 倍（最小 5mm），
 * baseline 按已提交数递增堆叠。间距/方向的金标值待实机对照（V 注记）。
 *
 * @param candidate 当前候选。
 * @param committedCount 本序列已提交尺寸数。
 * @returns 标注局部坐标。
 */
export function resolveSmartDimensionSequenceLabel(
    candidate: SmartDimensionCandidate,
    committedCount: number,
): readonly [number, number] {
    const first = candidate.previewAnchors[0] ?? [0, 0];
    const second = candidate.previewAnchors[1] ?? first;
    const middle: readonly [number, number] = [(first[0] + second[0]) / 2, (first[1] + second[1]) / 2];
    const dx = second[0] - first[0];
    const dy = second[1] - first[1];
    const span = Math.hypot(dx, dy);

    if (span <= 1e-12) {
        return middle;
    }

    const offset = Math.max(span * 0.35, 0.005) * (1 + committedCount * 0.5);

    return [
        middle[0] + (-dy / span) * offset,
        middle[1] + (dx / span) * offset,
    ];
}
